// Suppress all console logs
// Toggle to enable/disable console logs globally
const ENABLE_CONSOLE_LOGS = true;

if (!ENABLE_CONSOLE_LOGS) {
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
  console.info = () => {};
  console.debug = () => {};
}

import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { ModalProvider, ModalRenderer } from "react-modal-state";
import api, { clearSession, isAuthRejected, loginPathFor, refreshSession } from "./utils/api"; // Import the API utility
// Page styles stay in the main bundle, in the same order as before route-level code
// splitting, so global rules and cascade order don't change when a page chunk loads.
import "./styles/style.css";
import "./styles/Results.css";
import "./components/pages/studentResults/StudentResults.css";
import "./components/sidebar/Sidebar.css";
import "./components/SkeletonLoader.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./styles/Dashboard.css";
import "./components/pages/assessmentResults/releaseStatus.css";
import "./components/pages/AssessmentQuestionAnswers.css";
import "./components/pages/AssessmentDetailTabs.css";
import "./components/pages/AppointmentBooking.css";
import "./components/pages/GuidanceDashboard.css";
import "./styles/AssessmentAISettings.css";
import "./components/pages/AssignTestsModal.css";
import "./components/pages/profile/Profile.css";
import AnnaToastContainer from "./components/common/toast/AnnaToastContainer";
import PageLoader from "./components/PageLoader";
import GuidesRedirect from "./components/globalSearch/GuidesRedirect";
import PageNotFound from "./components/pages/PageNotFound";import LandingPage from "./components/pages/LandingPage";
import CustomNavbar, { isStudentRoute } from "./components/pages/Navbar";
import { useLocation } from "react-router-dom";
import { ChatbotContext } from "./context/ChatbotContext";
import { UserContext } from "./context/UserContext";
import LoginModal from "./components/pages/LoginModal";
import RegisterModal from "./components/pages/RegisterModal";
import ForgotPasswordModal from "./components/pages/ForgotPasswordModal"; // Import the ForgotPasswordModal
import TermsConditionsModal from "./components/pages/TermsConditionsModal";
import SectionUpdateModal from "./components/SectionUpdateModal";
import InstallAppPrompt from "./pwa/InstallAppPrompt";
import { isStandalone } from "./pwa/platform";

import { LanguageProvider } from "./context/LanguageContext";

// Route pages load on demand. If a chunk can't be fetched (usually a tab left open
// across a deploy, so the old chunk files are gone), reload once to pick up the new build.
const CHUNK_RELOAD_KEY = "anna.chunkReloaded";
const lazyPage = (load) =>
  lazy(() =>
    load()
      .then((module) => {
        try {
          sessionStorage.removeItem(CHUNK_RELOAD_KEY);
        } catch {
          // Storage can be blocked; nothing to clear.
        }
        return module;
      })
      .catch((error) => {
        let alreadyReloaded = true;
        try {
          alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1";
          if (!alreadyReloaded) sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        } catch {
          // Without storage we can't guard against a reload loop, so don't reload.
        }
        if (alreadyReloaded) throw error;
        window.location.reload();
        return new Promise(() => {});
      }),
  );

const AccountDetails = lazyPage(() => import("./components/pages/AccountDetails"));
const Results = lazyPage(() => import("./components/pages/Results"));
const AdminLogin = lazyPage(() => import("./components/pages/GuidanceLogin"));
const Admin = lazyPage(() => import("./components/pages/Admin"));
const CourseOfferings = lazyPage(() => import("./components/pages/CourseOfferings"));
const Dashboard = lazyPage(() => import("./components/pages/Dashboard"));
const AppointmentsPage = lazyPage(() => import("./components/pages/appointments/AppointmentsPage"));
const Feedback = lazyPage(() => import("./components/pages/Feedback"));
const OperationsPage = lazyPage(() => import("./components/pages/operations/OperationsPage"));
const NotificationsPage = lazyPage(() => import("./components/notifications/NotificationsPage"));
const AuditLogPage = lazyPage(() => import("./components/pages/auditLog/AuditLogPage"));
const ProgramCodesPage = lazyPage(() => import("./components/pages/programCodes/ProgramCodesPage"));
const Conversation = lazyPage(() => import("./components/pages/Conversation"));
const ConversationDetails = lazyPage(() => import("./components/pages/ConversationDetails"));
const Management = lazyPage(() => import("./components/pages/Management"));
const AssessmentAISettings = lazyPage(() => import("./components/pages/AssessmentAISettings"));
const ProfilePage = lazyPage(() => import("./components/pages/profile/ProfilePage"));
const HiddenAdmin = lazyPage(() => import("./components/pages/HiddenAdmin"));
const ResetPasswordPage = lazyPage(() => import("./components/pages/ResetPasswordPage"));
const SetPasswordPage = lazyPage(() => import("./components/pages/SetPasswordPage"));
const GuidanceDashboard = lazyPage(() => import("./components/pages/GuidanceDashboard"));
const Appointments = lazyPage(() => import("./components/pages/Appointments"));
const AppointmentBooking = lazyPage(() => import("./components/pages/AppointmentBooking"));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAgreeTermsConditions, setIsAgreeTermsConditions] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showBot, setShowbot] = useState(true);
  const inputRef = useRef(null);
  const [disabledInput, setDisabledInput] = useState(false);
  const [isVisibleInput, setIsVisibleInput] = useState(true);
  const [botChatLoading, setBotChatLoading] = useState(false);
  const [isSidebarActive, setIsSidebarActive] = useState(false);
  const [isRecommendationProvided, setIsRecommendationProvided] = useState({
    riasec: "",
    strand: "",
  });
  const [basis, setBasis] = useState("");
  const [user, setUser] = useState({}); // Add user state
  const [sectionUpdateStatus, setSectionUpdateStatus] = useState(null);
  const [showSectionUpdateModal, setShowSectionUpdateModal] = useState(false);

  const checkSectionUpdateStatus = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSectionUpdateStatus(null);
      setShowSectionUpdateModal(false);
      return;
    }

    const isAdminPath = window.location.pathname.startsWith("/admin");
    if (isAdminPath) {
      setShowSectionUpdateModal(false);
      return;
    }

    try {
      const response = await api.get("/api/check_login_status/", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = response.data?.user_data || {};
      if (userData.is_staff || userData.is_superuser) {
        setShowSectionUpdateModal(false);
        setSectionUpdateStatus(null);
        return;
      }
      setSectionUpdateStatus(userData);
      setShowSectionUpdateModal(Boolean(userData.requires_section_update));
    } catch (error) {
      console.error("Error checking section update status:", error);
    }
  };

  const fetchUserData = async () => {
    const token = localStorage.getItem("authToken");
    if (token) {
      try {
        const response = await api.get("/auth/user/", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.status === 200) {
          setUser(response.data); // Update the user state
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
        if (isAuthRejected(error)) {
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("isAuthenticated");
          setUser({});
        }
      }
    }
  };
  useEffect(() => {
    fetchUserData();
  }, []);

  // Initialize Google Analytics dynamically
  useEffect(() => {
    const tenantDomain = window.location.hostname; // Get the current tenant's domain
    const isAuthenticated = localStorage.getItem("authToken")
      ? "registered"
      : "guest"; // Determine user type

    // Configure Google Analytics dynamically for each tenant
    window.gtag("config", "G-WBSVP6V9YQ", {
      send_page_view: false, // Prevent automatic page views
    });

    // Track the user type and tenant
    window.gtag("event", "page_view", {
      page_path: window.location.pathname,
      page_title: document.title,
      tenant: tenantDomain, // Add tenant-specific data
      userType: isAuthenticated, // Distinguish between registered and unregistered users
    });
  }, []);

  // Sign out: admins go to the admin sign-in on this same site, students to the landing page
  const handleLogout = () => {
    const loginPath = loginPathFor();
    clearSession();
    setIsAuthenticated(false);
    window.location.href = loginPath === "/admin/login" ? loginPath : "/";
  };

  // Shared with the API client: one refresh at a time, and the rotated refresh token is kept
  const refreshAccessToken = async () => {
    const before = localStorage.getItem("authToken");
    try {
      return await refreshSession();
    } catch (err) {
      console.error("Error refreshing access token:", err);
      // Another tab of this browser may have refreshed first (one stored session): keep its token
      const current = localStorage.getItem("authToken");
      if (current && current !== before) return current;
      if (err?.response) clearSession();
      return null;
    }
  };

  // Everyone signs in again once a day (shared school computers). The day is the local
  // one: it used to be the UTC date, which turns over at 8 AM in the Philippines, so the
  // first page load after 8 AM signed people out at the start of every working day.
  // The installed phone app is left out: it stays signed in (custom_auth/app_sessions.py).
  useEffect(() => {
    if (isStandalone()) return undefined;
    const removeAuthTokenDaily = (onLoad) => {
      const lastRemoved = localStorage.getItem("lastTokenRemoval");
      const today = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD, local time

      if (lastRemoved !== today) {
        localStorage.setItem("lastTokenRemoval", today);
        if (!localStorage.getItem("authToken") && !localStorage.getItem("refreshToken")) return;
        clearSession();
        // On load, verify() below sees no token and shows the signed-out app; no reload
        // needed. Mid-session, go to the right sign-in page.
        if (!onLoad) window.location.href = loginPathFor();
      }
    };

    // Run immediately on app load
    removeAuthTokenDaily(true);

    // Run every 12 hours
    const intervalId = setInterval(() => removeAuthTokenDaily(false), 12 * 60 * 60 * 1000); // 12 hours

    // Cleanup if component unmounts
    return () => clearInterval(intervalId);
  }, []);

  const verify = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const refreshToken = localStorage.getItem("refreshToken");

      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      const response = await api.get("/auth/is-verify/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 && response.data === true) {
        setIsAuthenticated(true);
        const userResponse = await api.get("/auth/user/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("👤 User data from verify:", userResponse.data);
        setUser(userResponse.data); // Fetch user data on verify
        await checkSectionUpdateStatus();
      } else if (refreshToken) {
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          setIsAuthenticated(true);
          const userResponse = await api.get("/auth/user/", {
            headers: { Authorization: `Bearer ${refreshedToken}` },
          });
          setUser(userResponse.data);
          await checkSectionUpdateStatus();
        } else {
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error("Auth verification failed:", err);
      setIsAuthenticated(false);
      if (isAuthRejected(err)) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("isAuthenticated");
        setUser({});
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verify(); // Verify authentication status on component mount
  }, []);

  useEffect(() => {
    // The sign-in dialog passes the login status it already fetched, so the section
    // check opens in the same moment as the chat (which waits behind it)
    const handleLoginSuccess = (event) => {
      const status = event.detail;
      if (status && "requires_section_update" in status) {
        setSectionUpdateStatus(status);
        setShowSectionUpdateModal(Boolean(status.requires_section_update));
      } else {
        checkSectionUpdateStatus();
      }
    };
    window.addEventListener("loginSuccess", handleLoginSuccess);
    return () => window.removeEventListener("loginSuccess", handleLoginSuccess);
  }, []);

  const handleSectionConfirmed = (data) => {
    setSectionUpdateStatus((prev) => ({
      ...(prev || {}),
      ...(data || {}),
      requires_section_update: false,
    }));
    setShowSectionUpdateModal(false);
    window.dispatchEvent(new Event("chatbotRefresh"));
  };

  // Staff who open the student side (landing page, student pages) go to the admin
  // dashboard. This used to sign them out, and since admin and student pages share one
  // stored session, that also signed out every open admin tab, whose next request then
  // landed on the student landing page.
  useEffect(() => {
    const sendStaffToAdmin = () => {
      const path = window.location.pathname;
      if (path.startsWith("/admin") || path.startsWith("/reset-password") || path.startsWith("/set-password")) return;
      if (!localStorage.getItem("authToken")) return;
      if (user && (user.is_staff || user.is_superuser)) {
        window.location.replace("/admin/dashboard");
      }
    };

    sendStaffToAdmin();
    window.addEventListener("popstate", sendStaffToAdmin);
    return () => window.removeEventListener("popstate", sendStaffToAdmin);
  }, [user]);

  const ChatbotContextValue = {
    isAgreeTermsConditions,
    setIsAgreeTermsConditions,
    showBot,
    setShowbot,
    hideChatbot: showSectionUpdateModal,
    disabledInput,
    setDisabledInput,
    inputRef,
    isVisibleInput,
    setIsVisibleInput,
    botChatLoading,
    setBotChatLoading,
    isRecommendationProvided,
    setIsRecommendationProvided,
    basis,
    setBasis,
  };

  const UserContextValue = {
    isAuthenticated,
    setIsAuthenticated,
    isSidebarActive,
    setIsSidebarActive,
    handleLogout,
    user,
    setUser,
    fetchUserData, // Allow other components to trigger a user update
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <LanguageProvider>
      <ChatbotContext.Provider value={ChatbotContextValue}>
        <UserContext.Provider value={UserContextValue}>
          <ModalProvider
            modals={[
              ["login", LoginModal],
              ["register", RegisterModal],
              ["forgotPassword", ForgotPasswordModal],
            ]}
          >
            <Router>
              <ConditionalNavbar />
              <AppRoutes isAuthenticated={isAuthenticated} />
              {/* Inside the router so signing in can navigate without a page reload */}
              <ModalRenderer Component={LoginModal} />
              <ModalRenderer Component={RegisterModal} />
              <ModalRenderer Component={ForgotPasswordModal} />
              {/* "Get the ANNA app" card for phones */}
              <InstallAppPrompt />
            </Router>
            <AnnaToastContainer />
            <SectionUpdateModal
              show={showSectionUpdateModal}
              status={sectionUpdateStatus}
              onConfirmed={handleSectionConfirmed}
            />
          </ModalProvider>
        </UserContext.Provider>
      </ChatbotContext.Provider>
    </LanguageProvider>
  );
}

// The student header shows on student pages only; admin, the landing page and
// public pages (reset password, 404) have their own layouts.
function ConditionalNavbar() {
  const location = useLocation();
  return isStudentRoute(location.pathname) ? <CustomNavbar /> : null;
}

function AppRoutes({ isAuthenticated }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Set LandingPage as the default */}
        <Route path="/" element={<LandingPage />} />

        {/* Admin protected route */}
        <Route
          path="/admin/*"
          element={
            isAuthenticated ? <Admin /> : <Navigate replace to="/admin/login" />
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="offerings" element={<CourseOfferings />} />
          <Route path="feedback" element={<Feedback />} />
          <Route path="operations" element={<OperationsPage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
          <Route path="program-codes" element={<ProgramCodesPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="conversation" element={<Conversation />} />
          <Route
            path="conversation/:conversationId"
            element={<ConversationDetails />}
          />
          <Route path="management" element={<Management />} />
          <Route path="assessment-ai" element={<AssessmentAISettings />} />
          {/* Help & FAQ became the guides in the top-bar search. */}
          <Route path="faq" element={<GuidesRedirect />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="hidden" element={<HiddenAdmin />} />
        </Route>

        {/* Redirect to dashboard if authenticated, otherwise show login */}
        <Route
          path="/admin/login"
          element={
            !isAuthenticated ? (
              <AdminLogin />
            ) : (
              <Navigate replace to="/admin/dashboard" />
            )
          }
        />

        {/* Other routes */}
        <Route path="/account" element={<AccountDetails />} />
        <Route path="/result" element={<Results />} />
        <Route path="/results" element={<Results />} />
        <Route path="/guidance-dashboard" element={<GuidanceDashboard />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/book-appointment" element={<AppointmentBooking />} />
        <Route
          path="/reset-password/:uidb64/:token/"
          element={<ResetPasswordPage />}
        />
        {/* "Set your password" link from welcome emails (custom_auth/welcome_links.py) */}
        <Route path="/set-password/:token" element={<SetPasswordPage />} />

        {/* Catch-all route for undefined paths */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
